import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Upload } from '../../ui/Icon';
import { tripPhotoUrl } from '../../../utils/tripPhotos';
import { LIGHT_GRADIENTS, STICKER_GRADIENTS } from './stickerSpec';
import type { TripPhoto } from '../../../types';

export interface BackgroundChoice {
  kind: 'photo' | 'upload' | 'gradient';
  photoId: string | null;
  gradient: string;
}

interface Props {
  photos: TripPhoto[];
  choice: BackgroundChoice;
  onChoice: (choice: BackgroundChoice) => void;
  onFile: (file: File) => void;
  hasUpload: boolean;
}

const TILE = 'w-14 h-20 rounded-lg overflow-hidden shrink-0 border-2 transition-colors cursor-pointer';

export default function StickerControls({
  photos, choice, onChoice, onFile, hasUpload,
}: Props) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);

  const isActive = (candidate: BackgroundChoice) =>
    choice.kind === candidate.kind
    && choice.photoId === candidate.photoId
    && (candidate.kind !== 'gradient' || choice.gradient === candidate.gradient);

  const tileClass = (active: boolean) =>
    `${TILE} ${active ? 'border-teal-500' : 'border-transparent hover:border-slate-300 dark:hover:border-slate-600'}`;

  const uploadActive = isActive({ ...choice, kind: 'upload', photoId: null });

  return (
    <div className="flex flex-col gap-5 min-w-0">
      <div>
        <p className="label">{t('trip.story.background')}</p>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {/* One tile, two jobs: it selects the picked image when another
              background is active, and re-opens the picker when it already is. */}
          <button
            type="button"
            onClick={() => (hasUpload && !uploadActive
              ? onChoice({ ...choice, kind: 'upload', photoId: null })
              : fileRef.current?.click())}
            className={`${tileClass(uploadActive)} bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500`}
            aria-label={t('trip.story.upload')}
            title={t('trip.story.upload')}
          >
            <Icon as={Upload} size={16} />
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*,.heic,.heif"
            className="hidden"
            onChange={e => {
              const file = e.target.files?.[0];
              // Reset, or picking the same file twice fires no change event.
              e.target.value = '';
              if (file) onFile(file);
            }}
          />

          {photos.map(photo => (
            <button
              key={photo.id}
              type="button"
              onClick={() => onChoice({ ...choice, kind: 'photo', photoId: photo.id })}
              className={tileClass(isActive({ ...choice, kind: 'photo', photoId: photo.id }))}
              aria-label={photo.originalName}
            >
              <img
                src={tripPhotoUrl(photo.thumbnailFilename ?? photo.filename)}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </button>
          ))}

          {Object.entries(STICKER_GRADIENTS).map(([key, gradient]) => {
            const active = isActive({ kind: 'gradient', photoId: null, gradient: key });
            // The cream swatch is all but invisible on the modal's white card,
            // so it keeps an outline even when it isn't the one selected.
            const outline = !active && LIGHT_GRADIENTS.includes(key) ? '!border-slate-200 dark:!border-slate-600' : '';
            return (
              <button
                key={key}
                type="button"
                onClick={() => onChoice({ kind: 'gradient', photoId: null, gradient: key })}
                className={`${tileClass(active)} ${outline}`}
                style={{ background: `linear-gradient(${gradient[0]}, ${gradient[1]})` }}
                aria-label={t(`trip.story.gradient.${key}`)}
                title={t(`trip.story.gradient.${key}`)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
